const AUDIENCES = ["customer", "internal"];
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|\+00:00)$/;

const SOURCE_KEYS = [
  "factId",
  "revision",
  "topicKey",
  "text",
  "language",
  "audience",
  "ownerRef",
  "sourceRef",
  "sourceRevision",
  "approvedBy",
  "reviewerRef",
  "approvedAt",
  "validFrom",
  "validUntil",
  "expectedRevision",
];

function snapshotValue(value) {
  if (value == null) {
    return "";
  }
  return String(value);
}

export function knowledgeSourceSnapshot(fields) {
  const source = fields && typeof fields === "object" ? fields : {};
  const snapshot = {};
  SOURCE_KEYS.forEach((key) => {
    snapshot[key] = snapshotValue(source[key]);
  });
  return snapshot;
}

function sameSnapshot(left, right) {
  return SOURCE_KEYS.every((key) => left[key] === right[key]);
}

export function resolveKnowledgeSourceCommand(previous, fields, createCommand) {
  const snapshot = knowledgeSourceSnapshot(fields);
  if (
    previous
    && typeof previous.commandId === "string"
    && previous.commandId !== ""
    && typeof previous.asOf === "string"
    && previous.asOf !== ""
    && previous.fields
    && sameSnapshot(previous.fields, snapshot)
  ) {
    return previous;
  }
  const created = createCommand();
  return {
    commandId: created.commandId,
    asOf: created.asOf,
    fields: snapshot,
  };
}

function requiredText(value) {
  if (typeof value !== "string" || value === "") {
    return null;
  }
  return value;
}

function wholeNumber(value) {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value < 0) {
      return undefined;
    }
    return value;
  }
  if (value == null || value === "") {
    return null;
  }
  if (typeof value !== "string" || !/^(0|[1-9]\d*)$/.test(value)) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
}

function utcInstant(value) {
  if (typeof value !== "string" || !UTC_INSTANT.test(value)) {
    return null;
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return value;
}

export function buildKnowledgeSourceBody({ commandId, asOf, fields }) {
  const source = fields && typeof fields === "object" ? fields : {};
  const factId = requiredText(source.factId);
  const topicKey = requiredText(source.topicKey);
  const text = requiredText(source.text);
  const language = requiredText(source.language);
  const audience = requiredText(source.audience);
  const ownerRef = requiredText(source.ownerRef);
  const sourceRef = requiredText(source.sourceRef);
  const sourceRevision = requiredText(source.sourceRevision);
  const approvedBy = requiredText(source.approvedBy);
  const reviewerRef = requiredText(source.reviewerRef);
  const approvedAt = utcInstant(source.approvedAt);
  const validFrom = utcInstant(source.validFrom);
  const validUntil = utcInstant(source.validUntil);
  const asOfInstant = utcInstant(asOf);
  const revision = wholeNumber(source.revision);
  const expectedRevision = wholeNumber(source.expectedRevision);
  if (
    typeof commandId !== "string"
    || commandId === ""
    || asOfInstant == null
    || factId == null
    || topicKey == null
    || text == null
    || language == null
    || audience == null
    || !AUDIENCES.includes(audience)
    || ownerRef == null
    || sourceRef == null
    || sourceRevision == null
    || approvedBy == null
    || reviewerRef == null
    || approvedAt == null
    || validFrom == null
    || validUntil == null
    || revision == null
    || expectedRevision === undefined
    || Date.parse(validUntil) <= Date.parse(validFrom)
  ) {
    return { error: "empty" };
  }
  const body = {
    command_id: commandId,
    as_of: asOfInstant,
    fact: {
      fact_id: factId,
      revision,
      topic_key: topicKey,
      approved_text_or_typed_value: text,
      language,
      audience,
      owner_ref: ownerRef,
      source_ref: sourceRef,
      source_revision: sourceRevision,
      approved_by: approvedBy,
      approved_at: approvedAt,
      reviewer_ref: reviewerRef,
      valid_from: validFrom,
      valid_until: validUntil,
      status: "approved",
    },
  };
  if (expectedRevision != null) {
    body.expected_revision = expectedRevision;
  }
  return { body };
}

export function knowledgeSourceSaveFollowUp({ outcome, status, reloadFailed, detail }) {
  if (outcome === "applied" || outcome === "replayed") {
    if (reloadFailed) {
      return {
        saved: true,
        replaceList: false,
        notice: "Source change accepted. The list could not be reloaded.",
      };
    }
    return { saved: true, replaceList: true, notice: "Source change accepted." };
  }
  const refused = status === 403 || status === 409 || status === 422;
  const notice = refused && typeof detail === "string" && detail !== ""
    ? detail
    : "Source change was not saved.";
  if (refused && reloadFailed) {
    return {
      saved: false,
      replaceList: false,
      reload: false,
      notice: `${notice} The list could not be reloaded.`,
    };
  }
  return { saved: false, replaceList: false, reload: refused, notice };
}
